import { Cancel, Check, Close, Delete, Edit, Info } from "@mui/icons-material";
import { Button, CircularProgress, IconButton, TextField, Tooltip } from "@mui/material";
import { useContext, useEffect, useState } from "react";

import DataContext from "../../../../../../../context/DataContext";
import { useDebouncedEffect } from "../../../../../../../hooks/useDebounceEffect";
import axios from "../../../../../../api/axios";
import ApiRequestErrorHandler from "../../../../../../global/ApiRequestErrorHandler";
import HtmlTooltip from "../../../../../../global/HtmlTooltip";
import DynamicInput from "../../../../../../global/InlineEditField/DynamicInput";

export default function AlternateContactRow({
  title,
  type,
  keyVal,
  typeKeyVal,
  contactLabel,
  apiUrl,
  optionsUrl,
  permissions,
  displayNameKey,
  customLimit,
  postKeyVal,
  trigger,
  setTrigger,
  optionsDisplayKey,
  valueHistory,
  displayDetail,
  valueLabels,
  disabled,
  nullable,
  staticVal,
  customDisplayUrl,
  deleteDisabled,
}) {
  const { userRoles } = useContext(DataContext);
  const [val, setVal] = useState("");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [hoverActive, setHoverActive] = useState(false);
  const [search, setSearch] = useState("");
  const [resLimit, setResLimit] = useState(25);
  const [showMoreVisible, setShowMoreVisible] = useState(false);
  const [activeEditObj, setActiveEditObj] = useState("");
  const [responseSuccess, setResponseSuccess] = useState(null);
  const [responseBreakdown, setResponseBreakdown] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const value = staticVal ? staticVal : displayDetail[keyVal];
    if (type === "multi-search-select" && value) {
      const customValArr = value.map((item) => valueLabels[item]);
      setVal(value);
      setActiveEditObj(customValArr);
    } else if (customDisplayUrl && valueLabels && value) {
      setVal(valueLabels[value][displayNameKey]);
      setActiveEditObj({
        [displayNameKey]: valueLabels[value][displayNameKey],
      });
    } else if (value !== "" && value !== null && valueLabels) {
      setVal(value);
      setActiveEditObj(
        optionsDisplayKey && value
          ? {
              [optionsDisplayKey]: value,
            }
          : valueLabels[keyVal] && displayNameKey && value
            ? {
                [displayNameKey]: valueLabels[keyVal][val],
              }
            : valueLabels[keyVal] && value
              ? {
                  label: valueLabels[keyVal][val],
                }
              : ""
      );
    } else {
      setVal("");
      setActiveEditObj("");
    }
    // eslint-disable-next-line
  }, [displayDetail, valueLabels, keyVal, optionsDisplayKey, trigger]);

  const handleApiError = (error) => {
    setLoading(false);
    const res = !error.response ? error.response : error.response.data;
    const errArr = ApiRequestErrorHandler(res);
    setResponseBreakdown(errArr[0]);
    setResponseSuccess(false);
  };

  const handleApiSuccess = (response, valueName, crudType) => {
    setLoading(false);
    const res = response;
    setResponseBreakdown(`${valueName} was successfully ${crudType}`);
    setResponseSuccess(true);
    setOpen(false);
    setTrigger(!trigger);
    setTimeout(() => {
      setResponseSuccess(null);
      setResponseBreakdown("");
    }, 5000);
    return res;
  };

  const submitData = (title) => {
    const postVal = val === "No Selection" || val === "" ? null : val;
    const postData = {
      [keyVal]: postVal,
    };

    axios
      .patch(apiUrl, postData, {
      })
      .then(function (response) {
        handleApiSuccess(response, title, "updated");
      })
      .catch(function (error) {
        handleApiError(error);
      });
  };

  const deleteData = () => {
    axios
      .delete(apiUrl, {
      })
      .then(function (response) {
        handleApiSuccess(response, title, "deleted");
      })
      .catch(function (error) {
        handleApiError(error);
      });
  };

  const cancelEdit = () => {
    const value = displayDetail[keyVal];
    if (type === "multi-search-select" && valueLabels) {
      setVal(value);
      setOpen(false);
      setResponseBreakdown("");
      setResponseSuccess(null);
    } else if (customDisplayUrl && valueLabels) {
      setVal(valueLabels[value][displayNameKey]);
      setActiveEditObj({
        [displayNameKey]: valueLabels[value][displayNameKey],
      });
      setOpen(false);
      setResponseBreakdown("");
      setResponseSuccess(null);
    } else {
      setVal(value ? value : "");
      setOpen(false);
      setResponseBreakdown("");
      setResponseSuccess(null);
    }
  };

  useDebouncedEffect(
    () => {
      if (
        userRoles.permissions.includes(
          !permissions ? "utilities.view_fieldoptions" : permissions
        ) &&
        optionsUrl &&
        open &&
        type !== "search-select"
      ) {
        axios
          .get(optionsUrl + `&limit=${resLimit}` + `${search ? `&search=${search}` : ""}`, {
          })
          .then((response) => {
            setOptions(response.data.results);
            if (response.data.count <= resLimit) {
              setShowMoreVisible(false);
            } else {
              setShowMoreVisible(true);
            }
          });
      } else {
        return;
      }
    },
    [userRoles.permissions, setOptions, search, resLimit, optionsUrl, open],
    250
  );

  return open ? (
    <div className="mb-2 w-full">
      <div className="mt-2 flex w-full items-center justify-between">
        <div className="w-[40%] self-center">
          <DynamicInput
            setVal={setVal}
            val={val}
            title={"Contact Label"}
            type={"select"}
            keyVal={typeKeyVal}
            caseDetail={displayDetail}
            options={options}
            apiUrl={optionsUrl}
            activeEditObj={activeEditObj}
            setSearch={setSearch}
            search={search}
            responseSuccess={responseSuccess}
            responseBreakdown={responseBreakdown}
            displayNameKey={displayNameKey}
            customLimit={customLimit}
            postKeyVal={postKeyVal}
            optionsDisplayKey={optionsDisplayKey}
            nullable={nullable}
            showMoreVisible={showMoreVisible}
            setResLimit={setResLimit}
            resLimit={resLimit}
          />
        </div>
        <div className="w-[60%] self-center">
          <TextField
            className="ml-2 mt-5 w-full"
            size="small"
            disabled={true}
            label={title}
            value={displayDetail[typeKeyVal]}
          />
        </div>
        {loading ? (
          <CircularProgress color="secondary" />
        ) : responseSuccess === false ? (
          <Cancel className="text-red-600" />
        ) : (
          ""
        )}
        {responseSuccess ? (
          ""
        ) : (
          <div className="flex w-[30%] flex-wrap justify-end">
            <Tooltip title="Submit">
              <IconButton className="text-green-600" onClick={() => submitData(title)} size="small">
                <Check className="text-[20px]" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                className="text-red-600"
                onClick={() => deleteData()}
                size="small"
                disabled={deleteDisabled}
              >
                <Delete className="text-[20px]" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton className="text-grey-600" onClick={() => cancelEdit()} size="small">
                <Close className="text-[20px]" />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>
      {responseSuccess === false && responseBreakdown ? (
        <p className="text-[10px] text-red-600">{responseBreakdown}</p>
      ) : (
        ""
      )}
    </div>
  ) : (
    <div>
      <div>
        <h2 className="text-gray-400">
          {title}: <span className="text-sky-600">{contactLabel}</span>
          {valueHistory ? (
            <HtmlTooltip
              title={
                <div className="w-fit">
                  Previous value: {valueHistory["old_value"] ? valueHistory["old_value"] : "-"}
                  <br />
                  Updated at: {new Date(valueHistory["timestamp"]).toLocaleString()}
                  <br />
                  Updated by: {valueHistory["user"]}
                </div>
              }
              placement="right"
            >
              <Info className="text-[16px] text-black" />
            </HtmlTooltip>
          ) : (
            ""
          )}
        </h2>
        <Tooltip title="Click to edit">
          <Button
            onClick={() => {
              setOpen(true);
              setHoverActive(false);
            }}
            disabled={disabled}
            className="break-all text-left normal-case text-black hover:bg-gray-200"
            onMouseEnter={() => setHoverActive(true)}
            onMouseLeave={() => setHoverActive(false)}
          >
            {displayDetail[typeKeyVal]}{" "}
            <span className="h-[20px] w-[20px]">
              {hoverActive ? <Edit className="text-gray-400" /> : ""}
            </span>
          </Button>
        </Tooltip>
      </div>
      <p className="text-[12px]">
        {responseBreakdown} {responseSuccess ? <Check className="text-green-600" /> : ""}
      </p>
    </div>
  );
}
